import { FunctionComponent, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Dropdown } from "@/components/Dropdown/Dropdown";
import { Icon } from "@/components/Icon/Icon";

import { CountryMultiSelect } from "./CountryMultiSelect";
import { LanguagePicker } from "./LanguagePicker";

interface LanguageRuleConditionRowProps {
	field: "profile_language" | "personal_country";
	operator: "equals" | "is_on";
	value: string | string[];
	onChange: (updates: {
		field?: "profile_language" | "personal_country";
		operator?: "equals" | "is_on";
		value?: string | string[];
	}) => void;
	onRemove?: () => void;
}

export const LanguageRuleConditionRow: FunctionComponent<LanguageRuleConditionRowProps> = ({
	field,
	operator,
	value,
	onChange,
	onRemove,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`messages.languageAutoDetectRules.${key}`);

	const fieldOptions = useMemo(
		() => [
			{ id: "profile_language", title: ts("field.profileLanguage") },
			{ id: "personal_country", title: ts("field.personalCountry") },
		],

		[t]
	);

	const operatorOptions = useMemo(() => {
		if (field === "profile_language") {
			return [{ id: "equals", title: ts("operator.equals") }];
		}

		return [{ id: "is_on", title: ts("operator.isOn") }];
	}, [field, t]);

	const handleFieldChange = (item: { id: string | number; title: string }) => {
		const newField = item.id as "profile_language" | "personal_country";
		const newOperator: "equals" | "is_on" = newField === "profile_language" ? "equals" : "is_on";
		const newValue = newField === "profile_language" ? "" : [];

		onChange({ field: newField, operator: newOperator, value: newValue });
	};

	return (
		<div className="flex items-center gap-2">
			<Dropdown
				floating
				data={fieldOptions}
				defaultValue={fieldOptions.find((o) => o.id === field)}
				handleSelect={(item) => handleFieldChange(item as { id: string | number; title: string })}
			/>
			<Dropdown
				floating
				data={operatorOptions}
				defaultValue={operatorOptions.find((o) => o.id === operator)}
				handleSelect={(item) => onChange({ operator: item.id as "equals" | "is_on" })}
			/>
			{field === "profile_language" && (
				<LanguagePicker onChange={(code) => onChange({ value: code })} value={value as string} />
			)}
			{field === "personal_country" && (
				<CountryMultiSelect
					onChange={(names) => onChange({ value: names })}
					placeholder={ts("selectCountries")}
					selectedNames={Array.isArray(value) ? value : []}
				/>
			)}
			{onRemove && (
				<button
					aria-label={ts("removeCondition")}
					className="p-1 rounded hover:bg-background-light text-text-meta transition-colors shrink-0"
					data-testid="remove-condition"
					type="button"
					onClick={onRemove}
				>
					<Icon className="w-4 h-4" icon={faXmark} />
				</button>
			)}
		</div>
	);
};
